/* crm-realtime.jsx — Phase 6 Real-time Presence & Live Feed
   Provides: RealtimePresenceBar, LiveFeedPanel, useRealtimeEvents
   Self-registers on window. */

const { useState, useEffect, useRef, useCallback } = React;

import * as realtimeApi from '../../api/realtime.api.js';

// ─── useRealtimeEvents hook ───────────────────────────────────────────────────

const useRealtimeEvents = (workspaceId, types = []) => {
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);
  const esRef = useRef(null);

  useEffect(() => {
    if (!workspaceId) return;

    const es = realtimeApi.createEventSource(workspaceId);
    esRef.current = es;
    setError(null);

    es.onmessage = (e) => {
      try {
        const ev = JSON.parse(e.data);
        if (types.length === 0 || types.includes(ev.type)) {
          setEvents(prev => [ev, ...prev].slice(0, 50));
        }
      } catch {}
    };

    es.onerror = () => {
      setError('Real-time connection lost. Events may be delayed.');
      es.close();
    };

    return () => {
      es.close();
      esRef.current = null;
    };
  }, [workspaceId]);

  return { events, error };
};

// ─── Presence avatar ─────────────────────────────────────────────────────────

const PresenceAvatar = ({ user }) => {
  const initials = (user.name || '?').split(' ').map(p => p[0]).join('').slice(0, 2).toUpperCase();
  const colors = ['#6366f1', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#3b82f6'];
  const bg = colors[initials.charCodeAt(0) % colors.length];

  return (
    <div title={`${user.name}${user.entityType ? ` — viewing ${user.entityType}` : ''}`} style={{
      width: 28, height: 28, borderRadius: '50%', background: bg,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 10, fontWeight: 700, color: '#fff',
      border: '2px solid var(--nx-surface)', cursor: 'default',
      flexShrink: 0,
    }}>{initials}</div>
  );
};

// ─── Realtime Presence Bar ────────────────────────────────────────────────────

const RealtimePresenceBar = ({ workspaceId }) => {
  const [presence, setPresence] = useState([]);
  const [error, setError] = useState(null);
  const timerRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const data = await realtimeApi.getPresence(workspaceId);
      setPresence(data?.users || []);
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to load presence');
    }
  }, [workspaceId]);

  useEffect(() => {
    refresh();
    timerRef.current = setInterval(refresh, 30_000);
    return () => clearInterval(timerRef.current);
  }, [refresh]);

  if (error) return (
    <div style={{ padding: '4px 12px', color: 'var(--nx-error, #ef4444)', fontSize: 11 }}>{error}</div>
  );
  if (!presence.length) return null;

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 6, padding: '4px 12px',
      background: 'var(--nx-surface-alt)', borderBottom: '1px solid var(--nx-border)',
    }}>
      <span style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginRight: 4 }}>Active now:</span>
      <div style={{ display: 'flex', gap: -4 }}>
        {presence.slice(0, 8).map(u => (
          <div key={u.userId} style={{ marginLeft: presence.indexOf(u) > 0 ? -6 : 0 }}>
            <PresenceAvatar user={u} />
          </div>
        ))}
        {presence.length > 8 && (
          <div style={{
            width: 28, height: 28, borderRadius: '50%', background: 'var(--nx-border)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 10, color: 'var(--nx-text-muted)', marginLeft: -6,
          }}>+{presence.length - 8}</div>
        )}
      </div>
    </div>
  );
};

// ─── Live Feed Panel ──────────────────────────────────────────────────────────

const EVENT_ICONS = {
  'deal.created': '✦',
  'deal.updated': '✎',
  'deal.won': '★',
  'deal.lost': '✕',
  'person.created': '✦',
  'activity.created': '◆',
  'presence.update': '●',
  default: '·',
};

const LiveFeedPanel = ({ workspaceId }) => {
  const { events, error } = useRealtimeEvents(workspaceId);

  if (error) return (
    <div style={{ padding: '20px 16px', color: 'var(--nx-error, #ef4444)', fontSize: 13, textAlign: 'center' }}>{error}</div>
  );
  if (!events.length) return (
    <div style={{ padding: '20px 16px', color: 'var(--nx-text-muted)', fontSize: 13, textAlign: 'center' }}>
      Listening for live updates…
    </div>
  );

  return (
    <div style={{ maxHeight: 400, overflowY: 'auto' }}>
      {events.map((ev, i) => (
        <div key={i} style={{
          display: 'flex', gap: 10, padding: '8px 12px',
          borderBottom: '1px solid var(--nx-border)', alignItems: 'flex-start',
        }}>
          <span style={{ fontSize: 14, color: 'var(--nx-accent)', marginTop: 1, flexShrink: 0 }}>
            {EVENT_ICONS[ev.type] || EVENT_ICONS.default}
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 600 }}>{ev.type}</div>
            {ev.payload?.name && (
              <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {ev.payload.name}
              </div>
            )}
          </div>
          <span style={{ fontSize: 10, color: 'var(--nx-text-muted)', flexShrink: 0 }}>
            {ev.timestamp ? new Date(ev.timestamp).toLocaleTimeString() : 'just now'}
          </span>
        </div>
      ))}
    </div>
  );
};

// ─── Realtime Dashboard View ─────────────────────────────────────────────────

const RealtimeDashboardView = ({ workspaceId }) => (
  <div style={{ padding: '16px 20px' }}>
    <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 16 }}>Real-time Activity</div>
    <RealtimePresenceBar workspaceId={workspaceId} />
    <div style={{ marginTop: 16, background: 'var(--nx-surface-alt)', border: '1px solid var(--nx-border)', borderRadius: 10 }}>
      <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--nx-border)', fontWeight: 600, fontSize: 13 }}>Live Feed</div>
      <LiveFeedPanel workspaceId={workspaceId} />
    </div>
  </div>
);

window.RealtimeDashboardView = RealtimeDashboardView;
window.RealtimePresenceBar = RealtimePresenceBar;
window.LiveFeedPanel = LiveFeedPanel;
window.useRealtimeEvents = useRealtimeEvents;
